import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Leaf, Send, CheckCircle } from 'lucide-react';

interface RequestAccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RequestAccessModal({ open, onOpenChange }: RequestAccessModalProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { error } = await (supabase as any)
        .from('access_requests')
        .insert({
          email: email.trim(),
          name: name.trim() || null,
          reason: reason.trim() || null,
        });
      if (error) throw error;
      setSubmitted(true);
    } catch (err: any) {
      toast.error(err.message || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setTimeout(() => {
        setSubmitted(false);
        setEmail('');
        setName('');
        setReason('');
      }, 300);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md rounded-2xl">
        {submitted ? (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl gradient-primary shadow-lg shadow-primary/25">
              <CheckCircle className="h-8 w-8 text-white" />
            </div>
            <div>
              <h3 className="font-display text-xl font-bold text-foreground">Request Submitted!</h3>
              <p className="text-sm text-muted-foreground mt-2 max-w-xs">
                We'll review your request and get back to you at{' '}
                <span className="font-medium text-foreground">{email}</span>.
              </p>
            </div>
            <Button
              onClick={() => handleClose(false)}
              className="rounded-xl gradient-primary border-0 px-8 mt-2"
            >
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-1">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl gradient-primary shadow-sm">
                  <Leaf className="h-5 w-5 text-white" />
                </div>
                <DialogTitle className="font-display text-xl">Request Access</DialogTitle>
              </div>
              <DialogDescription>
                Velora is in early access. Tell us about yourself and we'll reach out soon.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <Label htmlFor="req-email">Email address *</Label>
                <Input
                  id="req-email"
                  type="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="req-name">Your name</Label>
                <Input
                  id="req-name"
                  type="text"
                  placeholder="Jane Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="req-reason">Why do you want access?</Label>
                <Textarea
                  id="req-reason"
                  placeholder="Tell us a bit about your use case…"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="rounded-xl resize-none"
                  rows={3}
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full rounded-xl gradient-primary border-0 gap-2"
              >
                {loading ? 'Submitting…' : (
                  <><Send className="h-4 w-4" /> Submit Request</>
                )}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
